import React, { useState } from 'react'
import {
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import TickIcon from '@material-ui/icons/Check'

import EndIconButton from '../EndIconButton'
import RichText from '../RichText'
import { useStyles } from './PageForm.styles'
import { formFieldValidation } from '../../utils/formValidation'
import MobileNumberInput from '../MobileNumberInput/MobileNumberInput'
import { contentfulArrayToObject } from '../../utils/cms'
import { REGIONS, REPORT_TYPES } from './PageForm.data'
import { useFormService } from '../../services/formWithRecaptchaService'
import { prependCountryCode } from '../../utils/countryCode'
import { MailtoIcon } from '../SVGIcons'
import { GOOGLE_LINKS } from '../../constants'

const DEFAULT_FORM_STATE = {}

const getStartIconForButton = (submitted, isFooterForm, loading) => {
  if (submitted) return <TickIcon fill="#fff" />
  if (loading) return <CircularProgress size={24} />
  if (isFooterForm) return undefined
  return <MailtoIcon />
}

const PageForm = ({ data = {}, source, t, isFooterForm }) => {
  const styles = useStyles()

  const { formFields: formData, optionalFormFields } = data

  const formFields = [
    ...formData.map((field) => ({ ...field, isRequired: true })),
    ...(optionalFormFields || []),
  ]

  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [form, setForm] = useState(DEFAULT_FORM_STATE)
  const [fieldValidation, setFieldValidation] = useState({})

  // FIXME: temporary disable file-upload
  // const [file, setFile] = useState()

  const {
    name,
    message,
    email,
    number,
    selectRegion,
    reportType,
    // fileUpload,
    undefined: CTAs,
  } = contentfulArrayToObject(formFields, 'fieldType')

  const formCtas = Array.isArray(CTAs) ? CTAs : [CTAs]

  const submitButtonText = formCtas[0]?.ctaHeading || ''
  // FIXME: Use banners for success message
  //  const successMessageText = formCtas[1]?.ctaHeading || ''

  const hasError = (fieldName) =>
    Object.prototype.hasOwnProperty.call(fieldValidation, fieldName) &&
    !fieldValidation[fieldName] &&
    Object.keys(fieldValidation).length !== 0

  let path = 'phone'
  if (source?.includes('contact-us')) path = 'contactus'
  if (source?.includes('complaints')) path = 'complaint'
  if (source?.includes('whistle-blowing')) path = 'whistle-blower'

  const { handleFormSubmit } = useFormService(source, `/api/${path}`)
  // For clearing the form once submitted and starting the button animation
  const handleSubmit = async () => {
    const { validateEntries, status } = formFieldValidation(form)

    if (status) {
      setFieldValidation(validateEntries)

      const requestData = {
        ...form,
        ...(form.phone && { 'phone-number': prependCountryCode(form.phone) }),
      }
      delete requestData.phone

      if (path === 'phone') requestData.source = source

      setLoading(true)
      try {
        /*
          FIXME: temporary disable file-upload
          handleFormSubmit(requestData, file) to handleFormSubmit(requestData)
        */

        const response = await handleFormSubmit(requestData)
        if (!response.ok) {
          throw new Error('API Call failed')
        }
        setLoading(false)
        setSubmitted(true)
        setForm(DEFAULT_FORM_STATE)
        if (
          typeof window !== 'undefined' &&
          // eslint-disable-next-line no-undef
          typeof window.gtag === 'function'
        ) {
          // eslint-disable-next-line no-undef
          window.gtag('event', 'phone_number_submitted', { ...data })
        }
      } catch (error) {
        setLoading(false)
      }
    } else {
      setFieldValidation(validateEntries)
    }
  }

  const isFormDisabled =
    (name?.isRequired && !form.name) ||
    (email?.isRequired && !form.email) ||
    (message?.isRequired && !form.message) ||
    (number?.isRequired && !form.phone) ||
    (selectRegion?.isRequired && form.region === undefined) ||
    (reportType?.isRequired && form['report-type'] === undefined)
  // fileUpload is never mandatory
  // (fileUpload?.isRequired && file === undefined)

  const renderFormField = (field) => {
    switch (field.fieldType) {
      case 'name':
        return (
          <TextField
            error={hasError('name')}
            onChange={(e) => {
              setForm({ ...form, name: e.target.value })
            }}
            value={form.name || ''}
            placeholder={field.placeholderText}
            color="secondary"
            helperText={hasError('name') ? field.validationText : ''}
            fullWidth
            className={styles.textField}
            isRequired={field.isRequired}
          />
        )
      case 'email':
        return (
          <TextField
            error={hasError('email')}
            onChange={(e) => {
              setForm({ ...form, email: e.target.value })
            }}
            value={form.email || ''}
            type={'email'}
            placeholder={field.placeholderText}
            color="secondary"
            helperText={hasError('email') ? field.validationText : ''}
            fullWidth
            className={styles.textField}
            isRequired={field.isRequired}
          />
        )
      case 'number':
        return (
          <MobileNumberInput
            error={hasError('phone')}
            onChange={(e) => {
              setForm({ ...form, phone: e.target.value })
            }}
            required
            helperText={hasError('phone') ? field.validationText : ''}
            className={[styles.textField, styles.phoneInput].join(' ')}
            placeholder={field.placeholderText}
            value={form.phone || ''}
            isRequired={field.isRequired}
          />
        )
      case 'message':
        return (
          <TextField
            onChange={(e) => {
              setForm({ ...form, message: e.target.value })
            }}
            value={form.message || ''}
            placeholder={field.placeholderText}
            error={hasError('message')}
            helperText={hasError('message') ? field.validationText : ''}
            color="secondary"
            fullWidth
            multiline={true}
            maxRows={7}
            className={styles.textField}
            isRequired={field.isRequired}
          />
        )
      case 'selectRegion':
        return (
          <TextField
            select
            onChange={(e) => {
              setForm({ ...form, region: e.target.value })
            }}
            placeholder={field.placeholderText}
            className={styles.textField}
            fullWidth
            value={form.region}
            label={field.placeholderText}
          >
            {REGIONS.map((region) => (
              <MenuItem key={region} value={region}>
                {t(region)}
              </MenuItem>
            ))}
            <MenuItem value={undefined} disabled>
              {field.placeholderText}
            </MenuItem>
          </TextField>
        )
      case 'reportType':
        return (
          <TextField
            select
            onChange={(e) => {
              setForm({ ...form, 'report-type': e.target.value })
            }}
            placeholder={field.placeholderText}
            className={styles.textField}
            fullWidth
            value={form['report-type']}
            label={field.placeholderText}
          >
            {REPORT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {t(type)}
              </MenuItem>
            ))}
            <MenuItem value={undefined} disabled>
              {field.placeholderText}
            </MenuItem>
          </TextField>
        )
      // FIXME: temporary disable file-upload
      // case 'fileUpload':
      //   return (
      //     <FileUpload
      //       text={field.placeholderText}
      //       errorLabel={field.validationText}
      //       t={t}
      //       onFileUpload={setFile}
      //     />
      //   )
      default:
        return <></>
    }
  }

  return (
    <Box
      component="form"
      className={styles.formContainer}
      onSubmit={handleSubmit}
    >
      {formFields.map((field) => {
        const propertyName = '__typename'
        switch (field[propertyName]) {
          case 'ContentfulFormField':
            return renderFormField(field)
          case 'ContentfulFormText':
            return (
              <RichText
                variant="body1"
                document={JSON.parse(field.formDescriptor.raw)}
              />
            )
          default:
            return <></>
        }
      })}
      {submitButtonText && (
        <EndIconButton
          startIcon={getStartIconForButton(submitted, isFooterForm, loading)}
          icon={false}
          className={styles.submitButton}
          title={submitButtonText}
          onClick={handleSubmit}
          disabled={isFormDisabled || submitted}
          description={
            <Typography variant="body2">
              {t(
                'This site is protected by reCAPTCHA and the Google {privacyPolicyLink} and {termsLink} apply',
                {
                  privacyPolicyLink: (
                    <a
                      href={GOOGLE_LINKS.privacyPolicy}
                      target="blank"
                      rel="noopener noreferrer"
                    >
                      {t('Privacy Policy')}
                    </a>
                  ),
                  termsLink: (
                    <a
                      href={GOOGLE_LINKS.termsOfService}
                      target="blank"
                      rel="noopener noreferrer"
                    >
                      {t('Terms & Conditions')}
                    </a>
                  ),
                },
              )}
            </Typography>
          }
        />
      )}
    </Box>
  )
}

export default PageForm
